<script lang="ts">
  import { navigate } from 'svelte-routing';
  import { createEventDispatcher } from 'svelte';
  import AgeSelector from '$lib/components/AgeSelector.svelte';

  const dispatch = createEventDispatcher();

  function handlePlay(event: CustomEvent<{ year: number; month: number }>) {
    const { year, month } = event.detail;
    navigate(`/${year}/${month}`);
    dispatch('navigate');
  }
</script>

<svelte:head>
  <title>outlive.it - An idle game that takes 104 years to complete</title>
</svelte:head>

<!-- Banner -->
<section id="intro" class="main">
  <AgeSelector on:play={handlePlay} />
</section>

<!-- Items -->
<section class="main items">
  <article class="item item--featured">
    <header>
      <a
        href="/"
        on:click|preventDefault
        style:cursor="initial"
        style:background-image="url('/assets/images/book.webp')"
        aria-hidden="true"
        tabindex="-1"
      ></a>
      <h3>How does this work?</h3>
    </header>
    <div class="featured-content">
      <p>
        We meticulously scour through our Ecclesiastical Database and compare the Papal Records
        to your age. You will see your ranking and progress.
      </p>
      <p>
        You can subscribe to email notifications. They are sent whenever you outlive a pope or an antipope.
        You will get at most 302 emails over 104 years, so on average one every four months.
      </p>
      <p>
        Pope John XI died the youngest at the age of 25, so consider your first 25 years as tutorial to the game.
      </p>
    </div>
  </article>

  <article class="item item--small">
    <header>
      <a
        href="/feedback"
        style:background-image="url('/assets/images/mailbox.webp')"
        aria-label="Feedback"
        on:click|preventDefault={() => navigate('/feedback')}
      ></a>
      <h3>Feedback</h3>
    </header>
  </article>

  <article class="item item--small">
    <header>
      <a
        href="/about"
        style:background-image="url('/assets/images/books.webp')"
        aria-label="About this game"
        on:click|preventDefault={() => navigate('/about')}
      ></a>
      <h3>About</h3>
    </header>
  </article>

  <article class="item item--small">
    <header>
      <a
        href="/privacy"
        style:background-image="url('/assets/images/privacy.webp')"
        aria-label="Privacy"
        on:click|preventDefault={() => navigate('/privacy')}
      ></a>
      <h3>Privacy</h3>
    </header>
  </article>
</section>

<style>
  .items {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 4px;
    row-gap: 4px;
  }

  .item--featured {
    grid-column: 1 / -1;
  }

  .item {
    padding: 3em 3em 1em 3em;
    background-color: #ffffff;
    border-radius: 4px;
    text-align: center;
  }

  .item--small {
    padding: 0;
  }

  .item :global(header) {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    margin: -3em 0 3em -3em;
    width: calc(100% + 6em);
    height: 15em;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #444;
    color: rgba(255, 255, 255, 0.75);
    position: relative;
    overflow: hidden;
  }

  .item--small :global(header) {
    border-radius: 4px;
    margin: 0;
    width: 100%;
    height: 100%;
    min-height: 9em;
  }

  .item :global(header a) {
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: block;
  }

  .item :global(header a::before) {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.25);
  }

  .item :global(header h3) {
    margin: 0;
    pointer-events: none;
    position: relative;
    z-index: 1;
    color: #ffffff;
  }

  .item--featured :global(header h3) {
    font-size: 2em;
  }

  .featured-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 2em;
    text-align: left;
  }

  .item--small :global(header h3) {
    border: 2px solid rgba(255, 255, 255, 0.85);
    padding: 0.4em 1.4em;
    border-radius: 2px;
    letter-spacing: 0.05em;
    transition: transform 0.2s ease;
    background: #00000088;
  }

  .item--small :global(header:hover h3) {
    color: #ef6480;
    border-color: #ef6480;
  }

  #intro {
    padding: 4em;
    background-color: #ffffff;
    border-radius: 4px;
    text-align: center;
  }

  @media screen and (max-width: 980px) {
    .item--small :global(header) {
      min-height: 9em;
    }
  }

  @media screen and (max-width: 736px) {
    .featured-content {
      grid-template-columns: 1fr;
    }

    .items {
      grid-template-columns: 1fr;
    }

    #intro {
      padding: 2em;
    }

    .item :global(header) {
      height: 15em;
    }

    .item--small :global(header) {
      height: 9em;
    }
  }

  @media screen and (max-width: 480px) {
    #intro {
      padding: 2em 1em;
    }

    .item {
      padding: 2em 1em 0.1em 1em;
    }

    .item--small {
      padding: 0;
    }

    .item :global(header) {
      height: 12em;
      margin: -2em 0 2em -1em;
      width: calc(100% + 2em);
    }

    .item--small :global(header) {
      height: 9em;
      margin: 0;
      width: 100%;
    }
  }
</style>
